/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { useRoom } from '../useRoom';
import { VideoProviderName } from '../../contexts/videoProvider/types';
import { notaryAPI } from '../../api/notary';
import { participantAPI } from '../../api/participant';
import { diagnostics } from '@utils/diagnostics';

/**
 * Fetches the video provider for the room before initilizing the VideoProvider Context for SigningRoom
 * @returns {{isLoading: boolean, videoProviderName: VideoProviderName}}
 */
export function usePreFetchVideoProvider(isNotary?: boolean) {
  const { roomId } = useRoom();

  const [videoProviderName, setVideoProviderName] = useState<VideoProviderName>(VideoProviderName.TWILIO);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        const videoProvider = isNotary
          ? (await notaryAPI.rooms.fetchCallDetailsForRoom(roomId)).videoProvider
          : (await participantAPI.rooms.fetchCallDetailsForRoom(roomId)).videoProvider;

        if (!Object.values(VideoProviderName).includes(videoProviderName)) {
          throw new Error('Invalid video provider');
        }
        setVideoProviderName(videoProvider as VideoProviderName);
      } catch (err: any) {
        diagnostics.error(new Error('Error fetching call details'), err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCallDetails();
  }, [roomId]);

  return { isLoading, videoProviderName };
}
